import React, { useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { CircleTickIcon, WarningCircleIcon } from 'images'
import { TransparentBgButton } from 'components/Shared/Button'
import { TestCampaignStatus } from './testCampaignUtils'

export default function PreviousCampaignBanner({ campaign, onViewDetails }) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  if (!campaign) return null

  const isSuccess = campaign.status === TestCampaignStatus.SUCCESS
  const isFailure = campaign.status === TestCampaignStatus.FAILURE

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleString()
    } catch {
      return dateString
    }
  }

  return (
    <div className="previous-campaign-banner">
      <div
        className={`previous-campaign-banner__header ${isSuccess ? 'success' : 'failure'}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="previous-campaign-banner__header-left">
          {isSuccess ? (
            <CircleTickIcon width={24} height={24} />
          ) : (
            <WarningCircleIcon width={24} height={24} />
          )}
          <div>
            <div className="previous-campaign-banner__title">
              {isSuccess
                ? t(
                  'Settings.MetaAccess.TestCampaign.previousSuccess',
                  'Previous test campaign succeeded',
                )
                : t(
                  'Settings.MetaAccess.TestCampaign.previousFailure',
                  'Previous test campaign failed',
                )}
            </div>
            {campaign.campaign_name && (
              <div className="previous-campaign-banner__campaign-name">
                {campaign.campaign_name}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          className="previous-campaign-banner__expand-btn"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <span
            style={{
              transitionDuration: '0.2s',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
            }}
            className={cn('arrow events-insights-page__status_menu_arrow', { open: isExpanded })}
          />
        </button>
      </div>

      {isExpanded && (
        <div className="previous-campaign-banner__content">
          <div className="previous-campaign-banner__details">
            {campaign.campaign_id && (
              <div className="previous-campaign-banner__detail-row">
                <span className="label">
                  {t('Settings.MetaAccess.TestCampaign.campaignId', 'Campaign ID')}:
                </span>
                <span className="value">{campaign.campaign_id}</span>
              </div>
            )}
            <div className="previous-campaign-banner__detail-row">
              <span className="label">
                {t('Settings.MetaAccess.TestCampaign.dateCompleted', 'Completed')}:
              </span>
              <span className="value">{formatDate(campaign.date_last_updated)}</span>
            </div>
            {isFailure && campaign.error && (
              <div className="previous-campaign-banner__detail-row">
                <span className="label">
                  {t('Settings.MetaAccess.TestCampaign.errorTitle', 'Error')}:
                </span>
                <span className="value error-text">{campaign.error.title}</span>
              </div>
            )}
          </div>

          <div className="previous-campaign-banner__actions">
            <TransparentBgButton
              text={t('Settings.MetaAccess.TestCampaign.viewDetails', 'View Details')}
              onClick={onViewDetails}
              color="navy"
              size="small"
            />
          </div>
        </div>
      )}
    </div>
  )
}

PreviousCampaignBanner.propTypes = {
  campaign: PropTypes.shape({
    campaign_id: PropTypes.string,
    campaign_name: PropTypes.string,
    status: PropTypes.string.isRequired,
    error: PropTypes.object,
    date_last_updated: PropTypes.string,
  }),
  onViewDetails: PropTypes.func.isRequired,
}
